import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next'; // Ավելացված է
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faPhone, faCalendarAlt, 
    faTimes, faIdBadge, faComments, 
    faPaperclip, faArrowUp, faInbox
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';
import AdminMessageReply from './AdminMessageReply';

// --- SUB-COMPONENTS ---

const AnalyticCard = ({ title, value, icon, grad, activeText }) => (
    <div style={{...styles.analyticCard, background: grad}}>
        <div style={styles.anaIcon}><FontAwesomeIcon icon={icon} /></div>
        <div>
            <p style={styles.anaTitle}>{title}</p>
            <h3 style={styles.anaValue}>{value}</h3>
        </div>
        <div style={styles.anaTrend}><FontAwesomeIcon icon={faArrowUp} /> {activeText}</div>
    </div>
);

const InfoBox = ({ icon, label, val, fallback }) => (
    <div style={styles.infoCard}>
        <div style={styles.infoIconBox}><FontAwesomeIcon icon={icon} /></div>
        <div style={{textAlign: 'left'}}>
            <p style={styles.infoLabel}>{label}</p>
            <p style={styles.infoVal}>{val || fallback}</p>
        </div>
    </div>
);

const AdminMessages = ({ list = [], refresh }) => {
    const { t, i18n } = useTranslation(); // Ավելացված է
    const [selectedUser, setSelectedUser] = useState(null);

    const getHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true'
        };
    };

    const stats = useMemo(() => ({
        total: list.length,
        today: list.filter(m => new Date(m.created_at).toDateString() === new Date().toDateString()).length,
        withAttachments: list.filter(m => m.attachment).length
    }), [list]);

    const fetchFullUserData = async (msg) => {
        try {
            const res = await fetch(`${API_BASE}/api/admin-users`, { 
                headers: getHeaders() 
            });
            
            if (res.status === 401 || res.status === 403) {
                alert(t('session_expired_error'));
                return;
            }

            const allUsers = await res.json();
            const foundUser = Array.isArray(allUsers) 
                ? allUsers.find(u => u.id === msg.user_id || u.phone === msg.phone) 
                : null;
            
            setSelectedUser(foundUser || { 
                full_name: msg.name, 
                phone: msg.phone, 
                profile_pic: msg.profile_pic,
                id: msg.user_id || t('guest_user'),
                created_at: msg.created_at
            });
        } catch (e) {
            console.error("Error fetching user data:", e);
            setSelectedUser(msg);
        }
    };

    const deleteMessage = async (id) => {
        if (window.confirm(t('delete_message_confirm'))) {
            try {
                const res = await fetch(`${API_BASE}/api/messages/${id}`, { 
                    method: 'DELETE',
                    headers: getHeaders()
                });
                if (res.ok) {
                    refresh();
                } else {
                    alert(t('delete_failed'));
                }
            } catch (e) { 
                console.error("Error deleting:", e); 
            }
        }
    };

    // Օգնող ֆունկցիա ամսաթվի լոկալիզացիայի համար
    const formatDate = (dateStr, includeTime = true) => {
        const date = new Date(dateStr);
        const locale = i18n.language === 'am' ? 'hy-AM' : i18n.language === 'ru' ? 'ru-RU' : 'en-US';
        return includeTime ? date.toLocaleString(locale) : date.toLocaleDateString(locale);
    };

    return (
        <div style={styles.page}>
            <div style={styles.analyticsRow}>
                <AnalyticCard 
                    title={t('stats_total_messages')} 
                    value={stats.total} 
                    icon={faComments} 
                    grad="linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)" 
                    activeText={t('status_active')}
                />
                <AnalyticCard 
                    title={t('stats_today_messages')} 
                    value={stats.today} 
                    icon={faInbox} 
                    grad="linear-gradient(135deg, #FF9933 0%, #FFB366 100%)" 
                    activeText={t('status_active')}
                />
                <AnalyticCard 
                    title={t('stats_with_files')} 
                    value={stats.withAttachments} 
                    icon={faPaperclip} 
                    grad="linear-gradient(135deg, #01B574 0%, #2AF598 100%)" 
                    activeText={t('status_active')}
                />
            </div>

            <h2 style={styles.sectionTitle}>{t('inbox_title')}</h2>

            <div style={styles.messagesGrid}>
                {list.map(m => (
                    <div key={m.id} style={styles.msgCard}>
                        <div style={styles.msgHeader}>
                            <div style={styles.avatarWrapper} onClick={() => fetchFullUserData(m)}>
                                {m.profile_pic ? (
                                  <img 
    src={m.profile_pic ? `${API_BASE}${m.profile_pic}` : '/assets/img/default-avatar.png'} 
    alt="avatar" 
    style={styles.avatarImg} 
    loading="lazy"      // 1. Բեռնել միայն այն մեսիջները, որոնք էկրանին են
    decoding="async"    // 2. Նկարի մշակումը չի դանդաղեցնի չատի աշխատանքը
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = 'https://cdn-icons-png.flaticon.com/512/149/149071.png'; 
    }} 
/>
                                ) : (
                                    <div style={styles.avatarPlaceholder}>{m.name?.[0]}</div>
                                )}
                            </div>
                            <div style={{ flex: 1, textAlign: 'left' }}>
                                <div style={styles.userName}>{m.name}</div>
                                <div style={styles.timeText}>
                                    <FontAwesomeIcon icon={faCalendarAlt} style={{marginRight: '5px'}} />
                                    {formatDate(m.created_at)}
                                </div>
                            </div>
                            {m.status === 'replied' && <span style={styles.repliedBadge}>{t('badge_replied')}</span>}
                        </div>

                        <div style={styles.msgBody}>{m.message}</div>

                        {m.attachment && (
                            <div style={styles.attachmentWrapper}>
                                <a href={`${API_BASE}${m.attachment}`} target="_blank" rel="noreferrer" style={styles.attachLink}>
                                    <img src={`${API_BASE}${m.attachment}`} alt="attachment" style={styles.msgImg} />
                                    <div style={styles.attachOverlay}><FontAwesomeIcon icon={faPaperclip} /> {t('view_file')}</div>
                                </a>
                            </div>
                        )}

                        <div style={styles.msgFooter}>
                            <div style={{ flex: 1 }}>
                                <AdminMessageReply messageId={m.id} onSuccess={refresh} />
                            </div>
                            <button onClick={() => deleteMessage(m.id)} style={styles.deleteBtn}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {selectedUser && (
                <div style={styles.modalOverlay} onClick={() => setSelectedUser(null)}>
                    <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.modalHeaderGrad}></div>
                        <button style={styles.closeModal} onClick={() => setSelectedUser(null)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                        
                        <div style={styles.bigAvatarWrapper}>
                            {selectedUser.profile_pic ? (
                                <img src={`${API_BASE}${selectedUser.profile_pic}`} style={styles.bigAvatarImg} alt="user" />
                            ) : (
                                <div style={styles.profileInitial}>{selectedUser.full_name?.[0] || selectedUser.name?.[0]}</div>
                            )}
                        </div>

                        <h2 style={styles.modalName}>{selectedUser.full_name || selectedUser.name}</h2>
                        <span style={styles.userBadge}>{t('customer_card')}</span>

                        <div style={styles.modalBody}>
                            <InfoBox icon={faPhone} label={t('label_phone')} val={selectedUser.phone} fallback={t('not_specified')} />
                            <InfoBox icon={faIdBadge} label={t('label_user_id')} val={`#${selectedUser.id}`} />
                            <InfoBox icon={faCalendarAlt} label={t('label_date')} val={formatDate(selectedUser.created_at, false)} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
// ... styles unchanged

// --- STYLES ---
const styles = {
    page: { padding: '30px', backgroundColor: '#F4F7FE', minHeight: '100vh' },
    analyticsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '40px' },
    sectionTitle: { fontSize: '24px', fontWeight: '800', color: '#1B2559', marginBottom: '25px', textAlign: 'left' },
    analyticCard: { padding: '25px', borderRadius: '24px', color: 'white', display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', overflow: 'hidden' },
    anaIcon: { width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' },
    anaTitle: { fontSize: '11px', opacity: 0.8, margin: 0, textTransform: 'uppercase', textAlign: 'left' },
    anaValue: { fontSize: '24px', fontWeight: '800', margin: '5px 0 0 0', textAlign: 'left' },
    anaTrend: { position: 'absolute', bottom: '10px', right: '15px', fontSize: '9px', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: '10px' },
    messagesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '25px' },
    msgCard: { background: '#fff', borderRadius: '24px', padding: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column' },
    avatarWrapper: { width: '50px', height: '50px', borderRadius: '15px', overflow: 'hidden', cursor: 'pointer' },
    avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    avatarPlaceholder: { width: '100%', height: '100%', background: '#4318FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' },
    msgHeader: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' },
    userName: { fontWeight: '800', color: '#1B2559', fontSize: '16px' },
    timeText: { fontSize: '12px', color: '#A3AED0' },
    repliedBadge: { background: '#D1FAE5', color: '#059669', fontSize: '10px', padding: '4px 8px', borderRadius: '8px', fontWeight: 'bold' },
    msgBody: { padding: '15px', background: '#F4F7FE', borderRadius: '16px', fontSize: '14px', color: '#475569', marginBottom: '15px', textAlign: 'left', lineHeight: '1.5' },
    attachmentWrapper: { marginBottom: '15px', borderRadius: '12px', overflow: 'hidden', position: 'relative' },
    attachLink: { display: 'block' },
    msgImg: { width: '100%', maxHeight: '150px', objectFit: 'cover' },
    attachOverlay: { position: 'absolute', bottom: 0, width: '100%', background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '5px', fontSize: '10px' },
    msgFooter: { display: 'flex', gap: '10px', marginTop: 'auto', alignItems: 'center' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '40px', height: '40px', borderRadius: '12px', cursor: 'pointer', flexShrink: 0 },
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { background: '#fff', width: '350px', borderRadius: '24px', position: 'relative', overflow: 'hidden', textAlign: 'center' },
    modalHeaderGrad: { height: '80px', background: 'linear-gradient(135deg, #4318FF 0%, #B233FF 100%)' },
    closeModal: { position: 'absolute', top: '10px', right: '10px', background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' },
    bigAvatarWrapper: { width: '100px', height: '100px', margin: '-50px auto 10px', borderRadius: '50%', border: '4px solid #fff', overflow: 'hidden', background: '#eee' },
    bigAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    profileInitial: { width: '100%', height: '100%', background: '#4318FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px' },
    modalName: { fontSize: '20px', color: '#1B2559', margin: '10px 0' },
    userBadge: { background: '#E6FAF5', color: '#01B574', padding: '2px 10px', borderRadius: '10px', fontSize: '10px', display: 'inline-block', marginBottom: '20px' },
    modalBody: { padding: '20px' },
    infoCard: { display: 'flex', gap: '15px', marginBottom: '12px', background: '#F4F7FE', padding: '12px', borderRadius: '16px', alignItems: 'center' },
    infoIconBox: { color: '#4318FF', width: '20px' },
    infoLabel: { fontSize: '10px', color: '#A3AED0', margin: 0, fontWeight: '600' },
    infoVal: { fontSize: '14px', fontWeight: 'bold', margin: 0, color: '#1B2559' }
};

export default AdminMessages;